<template>
    <kr-search
        ref="teachers_listing"
        name="teachers_listing"
        :url="{ url: search_url }"
        :columns="columns"
        :options="{
            action_column: false,
            table_caption: true,
            newServerMode: {
                includes: ['q', 'sort', 'order', 'email', 'firstname', 'lastname', 'isSuspended', 'level1Department', 'level2Department', 'assigned'],
            },
            default_query_fields: {
                sort: 'email',
                order: 'desc',
            },
            hideQuery: {
                includes: ['isTrashed', 'isArchived', 'isAtRisk', 'status', 'pageName'],
            },
            searchSuspended: true,
        }"
        @retrieve-data="getSearchData"
    >
        <template #upper-right>
            <router-link v-if="canAddTeacher" v-slot="{ navigate }" :to="{ name: 'teachers.create' }" custom>
                <button class="btn btn-success" :disabled="auth.isImpersonation() ? true : undefined" @click="navigate">
                    <i class="fa fa-plus marginRight8" aria-hidden="true"></i>New Staff
                </button>
            </router-link>
            <button
                v-if="canAddTeacher"
                class="btn btn-primary"
                :disabled="auth.isImpersonation() ? true : undefined"
                @click.prevent="$refs['UploadModalComponent'].upload()"
            >
                <i class="fa fa-file-import marginRight8" aria-hidden="true"></i>Import Staffs
            </button>

            <button class="btn btn-primary" @click="downloadTeachers"><i class="fa fa-download marginRight8" aria-hidden="true"></i>Export All Staffs</button>
        </template>
        <template #lower-right>
            <div class="flex gap10 marginBottom20">
                <DepartmentSelectVue3
                    v-if="isDepartmentEnabled"
                    ref="departmentAndAssignedSelectRef"
                    :options="departmentAndAssignedOptions"
                    :showSelection="true"
                    :editClasses="{
                        addClassToButton: ['padding10px'],
                        addClassToFirstList: ['fw-normal'],
                    }"
                    :level1Label="departmentLevels >= 1 ? departmentMeta.settings.level1.plural : null"
                    :level2Label="departmentLevels == 2 ? departmentMeta.settings.level2.plural : null"
                    :hideSelectLabel="true"
                    :showDropdownIcon="false"
                    :isFiltering="true"
                    @changeDepartment="
                        getQueryFilters($event);
                        $refs['teachers_listing'].debounced_search(true);
                    "
                />

                <div class="viewFilter">
                    <div class="form-group displayContent">
                        <div class="form-dropdown viewFilter marginLeft0">
                            <select
                                v-model="selectedOption"
                                class="form-control"
                                @change="
                                    $refs['teachers_listing'].debounced_search(true);
                                    changeValue();
                                "
                            >
                                <option value="">Firstname Lastname</option>
                                <option value="Lastname, Firstname">Lastname, Firstname</option>
                            </select>
                        </div>
                    </div>
                </div>
                <div class="viewFilter">
                    <div class="form-group displayContent">
                        <div class="form-dropdown viewFilter marginLeft0">
                            <select v-model="searchData.query.isSuspended" class="form-control" @change="$refs['teachers_listing'].debounced_search(true)">
                                <option value="0">Active</option>
                                <option value="1">Suspended</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="auth.user().role == 'Super Admin' && isDepartmentEnabled" class="flex marginBottom20">
                <div class="selected-count">
                    <p>{{ staffsSelected.length }} Selected</p>
                </div>
                <DepartmentSelectVue3
                    ref="departmentSelectRef"
                    :options="departmentOptions"
                    :isDisabled="staffsSelected.length == 0"
                    :disabledLabel="staffsSelected.length == 0 ? 'Please select a staff' : null"
                    :editClasses="{
                        addClassToButton: ['borderRadius4'],
                    }"
                    :level1Label="departmentLevels >= 1 ? departmentMeta.settings.level1.plural : null"
                    :level2Label="departmentLevels == 2 ? departmentMeta.settings.level2.plural : null"
                    :hideSelectLabel="true"
                    @changeDepartment="$refs['ConfirmationDepartmentModalComponent'].confirmation($event)"
                />
            </div>
        </template>

        <template #table-title="props">
            <p class="widthMaxContent">
                <b>
                    Showing {{ props.model.from }} - {{ props.model.to }} of
                    {{ props.model.meta.paginator.total }}
                </b>
            </p>
        </template>

        <template #table-caption> List of Staffs </template>

        <template #ndata="props">
            <p class="padding10">No staffs match your search / filter</p>
        </template>

        <template #cdata="props">
            <td class="py-9px">
                <div v-if="props.model.uuid != auth.user().uuid" class="dropdown" @click.prevent="toggleDropdown">
                    <button
                        :id="`dropdownMenuButton-${props.model.uuid}`"
                        v-tooltip="`More Actions`"
                        class="btn btn-outline-default icon-only dropdown-toggle"
                        data-toggle="dropdown"
                        aria-haspopup="true"
                        aria-expanded="false"
                        :aria-label="`More Actions for ${
                            selectedOption == 'Lastname, Firstname'
                                ? (props.model.lastname ? props.model.lastname + ' ' : '') + props.model.firstname
                                : props.model.firstname + (props.model.lastname ? ' ' + props.model.lastname : '')
                        }`"
                    >
                        <i class="fa-solid fa-ellipsis-vertical"></i>
                    </button>
                    <div class="dropdown-menu dropdown-menu-new zIndex3" aria-labelledby="dropdownMenuButton">
                        <ul>
                            <li>
                                <router-link
                                    class="primary-state"
                                    :to="{
                                        name: 'teachers.view',
                                        params: { id: props.model.uuid },
                                    }"
                                    :aria-labelledby="'View ' + props.model.firstname + (props.model.lastname ? ' ' + props.model.lastname : '') + ' profile'"
                                >
                                    <span> <i class="fas fa-eye marginRight8" aria-hidden="true"></i>View Teacher </span>
                                </router-link>
                            </li>
                            <li
                                v-if="
                                    canAddTeacher &&
                                    searchData.query.isSuspended == '0' &&
                                    ((auth.user().roleId == 3 && props.model.roleId != 2) || auth.user().roleId == 2)
                                "
                            >
                                <a
                                    class="primary-state"
                                    href="#"
                                    :aria-labelledby="'Reset ' + props.model.firstname + (props.model.lastname ? ' ' + props.model.lastname : '') + ' password'"
                                    @click.prevent="openResetPasswordModal(props.model)"
                                >
                                    <span> <i class="fa fa-key marginRight8" aria-hidden="true"></i>Reset Password </span>
                                </a>
                            </li>

                            <template v-if="auth.user().roleId < 3 || (auth.user().canAddTeacher && props.model.roleId == 3)">
                                <hr />

                                <li v-if="searchData.query.isSuspended == '0'">
                                    <a class="danger-state" href="#" @click.prevent="$refs['SuspendModalComponent'].suspend(props.model, 'teachers')">
                                        <span> <i class="fa fa-archive marginRight8" aria-hidden="true"></i>Suspend </span>
                                    </a>
                                </li>

                                <li v-else>
                                    <a class="primary-state" href="#" @click.prevent="$refs['ReinstateModalComponent'].reinstate(props.model, 'teachers')">
                                        <span> <i class="fa fa-refresh marginRight8" aria-hidden="true"></i>Reinstate </span>
                                    </a>
                                </li>
                            </template>
                        </ul>
                    </div>
                </div>
            </td>
            <td v-if="auth.user().role == 'Super Admin' && isDepartmentEnabled">
                <div class="checkboxField justifyCenter">
                    <input
                        type="checkbox"
                        :id="`select-${props.model.uuid}`"
                        v-model="staffsSelected"
                        :value="{
                            uuid: props.model.uuid,
                            name: props.model.firstname + ' ' + props.model.lastname,
                        }"
                    />
                </div>
            </td>
            <td>
                <template v-if="selectedOption == 'Lastname, Firstname'">
                    <router-link
                        v-if="props.model.uuid != auth.user().uuid"
                        v-tooltip="{ content: 'View Teacher' }"
                        v-dompurify-html="highlight((props.model.lastname ? props.model.lastname + ', ' : '') + props.model.firstname)"
                        tag="a"
                        :to="{
                            name: 'teachers.view',
                            params: { id: props.model.uuid },
                        }"
                        class="link"
                        :aria-label="'click to View teacher ' + (props.model.lastname ? props.model.lastname + ', ' : '') + props.model.firstname + ' profile'"
                    >
                    </router-link>
                    <router-link
                        v-else
                        v-tooltip="{ content: 'View Teacher' }"
                        v-dompurify-html="highlight((props.model.lastname ? props.model.lastname + ', ' : '') + props.model.firstname)"
                        tag="a"
                        :to="{
                            name: 'profile.view',
                            params: { id: props.model.uuid },
                        }"
                        class="link"
                        :aria-label="'click to View teacher' + (props.model.lastname ? props.model.lastname + ', ' : '') + props.model.firstname + ' profile'"
                    >
                    </router-link>
                </template>
                <template v-else>
                    <router-link
                        v-if="props.model.uuid != auth.user().uuid"
                        v-tooltip="{ content: 'View Teacher' }"
                        v-dompurify-html="highlight(props.model.firstname + (props.model.lastname ? ' ' + props.model.lastname : ''))"
                        tag="a"
                        :to="{
                            name: 'teachers.view',
                            params: { id: props.model.uuid },
                        }"
                        class="link"
                        :aria-label="'click to View teacher ' + props.model.firstname + (props.model.lastname ? ' ' + props.model.lastname : '') + ' profile'"
                    >
                    </router-link>
                    <router-link
                        v-else
                        v-tooltip="{ content: 'View Teacher' }"
                        v-dompurify-html="highlight(props.model.firstname + (props.model.lastname ? ' ' + props.model.lastname : ''))"
                        tag="a"
                        :to="{
                            name: 'profile.view',
                            params: { id: props.model.uuid },
                        }"
                        class="link"
                        :aria-label="'click to View teacher' + props.model.firstname + (props.model.lastname ? ' ' + props.model.lastname : '') + ' profile'"
                    >
                    </router-link>
                </template>
            </td>
            <td v-if="!isMobileView" :aria-label="'teacher role ' + props.model.role">
                <template v-if="props.model.role == 'Super Admin'"> Super Admin </template>
                <template v-else-if="props.model.canAddTeacher"> Admin Teacher </template>
                <template v-else> Teacher </template>
            </td>
            <td v-if="!isMobileView && isDepartmentEnabled">
                <template v-if="props.model.department.level1">
                    {{ props.model.department.level1 }}
                </template>
                <template v-else> - </template>
            </td>
            <td v-if="!isMobileView && isDepartmentEnabled && props.model.department.level == 2">
                <template v-if="props.model.department.level2">
                    {{ props.model.department.level2 }}
                </template>
                <template v-else> - </template>
            </td>
            <td
                v-if="!isMobileView"
                v-dompurify-html="highlight(props.model.email ? props.model.email : '-')"
                :arial-label="`teacher email ${props.model.email ? props.model.email : 'none'}`"
            ></td>
            <td v-if="auth.user().roleId == 2" class="hidden-xs hidden-sm" :aria-label="`${searchData.query.isSuspended == '0' ? 'Active' : 'Suspend'}`">
                <template v-if="searchData.query.isSuspended == '0'">
                    <i class="fa fa-circle activeCircle marginRight8" aria-hidden="true"></i>Active
                </template>
                <template v-else> <i class="fa fa-circle suspendCircle marginRight8" aria-hidden="true"></i>Suspended </template>
            </td>
        </template>
    </kr-search>

    <suspendModalComponent ref="SuspendModalComponent" @user-suspended="user.isSuspended = $event" />
    <reinstateModalComponent ref="ReinstateModalComponent" @user-reinstated="user.isSuspended = $event" />
    <uploadModalComponent ref="UploadModalComponent" />
    <confirmationDepartmentModal
        ref="ConfirmationDepartmentModalComponent"
        :staffsSelected="staffsSelected"
        :departmentMeta="departmentMeta"
        @emptySelectedStaffs="cleanDepartment()"
    />
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';

import KrAuth from '../../../../../components/auth/auth';
import KrForm from '../../../../../components/forms/form';
import { useDownload } from '@/composables/helpers/useDownload.ts';
import suspendModalComponent from '../../actions/suspendModal.vue';
import reinstateModalComponent from '../../actions/reinstateModal.vue';
import uploadModalComponent from '../../actions/upload.vue';
import confirmationDepartmentModal from '../../actions/confirmationDepartmentModal.vue';

const download = useDownload();
const auth = new KrAuth();

const component_done_loading = ref(false);
const columns = ref({});
const trash_field = ref(true);
const searchData = ref({
    meta: { paginator: { total: 0 }, filters: {} },
    query: { isSuspended: '0' },
    q: '',
});
const search_url = ref('/teachers');
const user = ref({});
const selectedOption = ref('');
const upload_form = new KrForm({
    fields: {
        data: '',
        //topic:'',
    },
    props: {},
    urls: {
        post: 'questions/store-uploaded',
    },
});

const staffsSelected = ref([]);
const departmentAndAssignedOptions = ref([]);
const departmentOptions = ref([]);
const departmentLevels = ref();
const departmentMeta = ref({});
const isDepartmentEnabled = ref(false);
const canAddTeacher = computed(() => {
    return auth.user().roleId == 2 || auth.user().canAddTeacher;
});

const departmentAndAssignedSelectRef = ref(null);

const getSearchData = (data) => {
    searchData.value = data;
};

const openResetPasswordModal = (user) => {
    Events.fire('open-reset-password-modal', {
        user: user,
        organisation: auth.user().organisationName,
    });
};

const highlight = (text) => {
    if (searchData.value.query.q != '') {
        if (text != null) {
            return text.replace(
                new RegExp(searchData.value.query.q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'),
                '<span class="table-keyword-highlight">$&</span>'
            );
        }
    } else {
        return text;
    }
};

const changeValue = () => {
    if (auth.user().roleId == 2) {
        if (selectedOption.value == '') {
            const propertyPosition = Object.keys(columns.value).indexOf('lastname');
            columns.value.firstname = columns.value.lastname;
            delete columns.value.lastname;
            columns.value = {
                ...Object.keys(columns.value)
                    .slice(0, propertyPosition)
                    .reduce((obj, key) => ((obj[key] = columns.value[key]), obj), {}),
                firstname: Object.values(columns.value)[propertyPosition],
                ...Object.keys(columns.value)
                    .slice(columns.value.length)
                    .reduce((obj, key) => ((obj[key] = columns.value[key]), obj), {}),
            };
            columns.value.firstname =
                'display:First Name|sortable:true|width:345px|minWidth:345px|maxWidth:345px|subDisplay:Last Name|subSortable:true|subSrc:lastname';
        } else {
            const propertyPosition = Object.keys(columns.value).indexOf('firstname');
            columns.value.lastname = columns.value.firstname;
            delete columns.value.firstname;
            columns.value = {
                ...Object.keys(columns.value)
                    .slice(0, propertyPosition)
                    .reduce((obj, key) => ((obj[key] = columns.value[key]), obj), {}),
                lastname: Object.values(columns.value)[propertyPosition],
                ...Object.keys(columns.value)
                    .slice(columns.value.length)
                    .reduce((obj, key) => ((obj[key] = columns.value[key]), obj), {}),
            };
            columns.value.lastname =
                'display:Last Name|sortable:true|width:345px|minWidth:345px|maxWidth:345px|subDisplay:First Name|subSortable:true|subSrc:firstname';
        }
    } else {
        if (selectedOption.value == '') {
            const propertyPosition = Object.keys(columns.value).indexOf('lastname');
            columns.value.firstname = columns.value.lastname;
            delete columns.value.lastname;
            columns.value = {
                ...Object.keys(columns.value)
                    .slice(0, propertyPosition)
                    .reduce((obj, key) => ((obj[key] = columns.value[key]), obj), {}),
                firstname: Object.values(columns.value)[propertyPosition],
                ...Object.keys(columns.value)
                    .slice(columns.value.length)
                    .reduce((obj, key) => ((obj[key] = columns.value[key]), obj), {}),
            };
            columns.value.firstname =
                'display:First Name|sortable:true|width:345px|minWidth:345px|maxWidth:345px|subDisplay:Last Name|subSortable:true|subSrc:lastname';
        } else {
            const propertyPosition = Object.keys(columns.value).indexOf('firstname');
            columns.value.lastname = columns.value.firstname;
            delete columns.value.firstname;
            columns.value = {
                ...Object.keys(columns.value)
                    .slice(0, propertyPosition)
                    .reduce((obj, key) => ((obj[key] = columns.value[key]), obj), {}),
                lastname: Object.values(columns.value)[propertyPosition],
                ...Object.keys(columns.value)
                    .slice(columns.value.length)
                    .reduce((obj, key) => ((obj[key] = columns.value[key]), obj), {}),
            };
            columns.value.lastname =
                'display:Last Name|sortable:true|width:345px|minWidth:345px|maxWidth:345px|subDisplay:First Name|subSortable:true|subSrc:firstname';
        }
    }
};

const downloadTeachers = () => {
    let d = moment().format('YYYY[_]MM[_]DD');
    axios({
        url: `teachers/export`,
        method: 'GET',
        responseType: 'blob', // important
    }).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'Teachers_' + d + '.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
    });
};

const getDepartment = () => {
    axios.get(`/departments`).then((response) => {
        const assignedData = [
            {
                id: 'allStaffs',
                name: 'All Staffs',
            },
            {
                id: 'assigned',
                name: 'Assigned Staffs',
            },
            {
                id: 'unassigned',
                name: 'Unassigned Staffs',
            },
        ];

        departmentMeta.value = response.data.meta;
        isDepartmentEnabled.value = departmentMeta.value.enabled;

        if (departmentMeta.value.enabled) {
            departmentLevels.value = departmentMeta.value.settings.level;

            departmentOptions.value = response.data.data;
            departmentAndAssignedOptions.value = [...assignedData, ...response.data.data];

            if (departmentLevels.value == 1) {
                columns.value.department = `display:${departmentMeta.value.settings.level1.plural}|sortable:false|mobile:hide|width:200px|minWidth:200px|maxWidth:200px`;
                delete columns.value.school;
            } else {
                columns.value.department = `display:${departmentMeta.value.settings.level1.plural}|sortable:false|mobile:hide|width:200px|minWidth:200px|maxWidth:200px`;
                columns.value.school = `display:${departmentMeta.value.settings.level2.plural}|sortable:false|mobile:hide|width:200px|minWidth:200px|maxWidth:200px`;
            }
        } else {
            delete columns.value.checkbox;
            delete columns.value.department;
            delete columns.value.school;
        }
    });
};

const init = () => {
    if (auth.user().roleId == 2) {
        columns.value = {
            action: 'display:Actions|sortable:false|width:90px|minWidth:90px|maxWidth:90px',
            checkbox: 'display:Checkbox|sortable:false|width:105px|minWidth:105px|maxWidth:105px',
            firstname: 'display:First Name|sortable:true|width:345px|minWidth:345px|maxWidth:345px|subDisplay:Last Name|subSortable:true|subSrc:lastname',
            role: 'display:Role|sortable:false|mobile:hide|width:160px|minWidth:160px|maxWidth:160px',
            department: 'display:Department|sortable:false|mobile:hide|width:200px|minWidth:200px|maxWidth:200px',
            school: 'display:School|sortable:false|mobile:hide|width:200px|minWidth:200px|maxWidth:200px',
            email: 'display:Email|sortable:true|mobile:hide|width:200px|minWidth:200px|maxWidth:200px',
            status: 'display:Status|sortable:false|mobile:hide|width:150px|minWidth:150px|maxWidth:150px',
        };
        trash_field.value = true;
    } else {
        columns.value = {
            action: 'display:Actions|sortable:false|width:90px|minWidth:90px|maxWidth:90px',
            checkbox: 'display:Checkbox|sortable:false|width:105px|minWidth:105px|maxWidth:105px',
            firstname: 'display:First Name|sortable:true|width:345px|minWidth:345px|maxWidth:345px|subDisplay:Last Name|subSortable:true|subSrc:lastname',
            role: 'display:Role|sortable:false|mobile:hide|width:160px|minWidth:160px|maxWidth:160px',
            department: 'display:Department|sortable:false|mobile:hide|width:200px|minWidth:200px|maxWidth:200px',
            school: 'display:School|sortable:false|mobile:hide|width:200px|minWidth:200px|maxWidth:200px',
            email: 'display:Email|sortable:true|mobile:hide|width:200px|minWidth:200px|maxWidth:200px',
        };
        trash_field.value = false;
    }

    if (auth.user().role != 'Super Admin') {
        delete columns.value.checkbox;
    }
};

const departmentSelectRef = ref(null);
const cleanDepartment = () => {
    staffsSelected.value = [];
    departmentSelectRef.value.onEmptySelect();
};

const getQueryFilters = (event) => {
    if (event.department.id == 'allStaffs' || event.department.id == 'assigned' || event.department.id == 'unassigned') {
        searchData.value.query.assigned = event.department.id == 'allStaffs' ? '' : event.department.id == 'assigned' ? 1 : 0;
        searchData.value.query.level1Department = '';
        searchData.value.query.level2Department = '';
    } else if (event.school != null) {
        searchData.value.query.assigned = '';
        searchData.value.query.level1Department = '';
        searchData.value.query.level2Department = event.school.id;
    } else {
        searchData.value.query.assigned = '';
        searchData.value.query.level1Department = event.department.id;
        searchData.value.query.level2Department = '';
    }
};

const toggleDropdown = (event) => {
    let dropdown = null;

    if (auth.user().roleId < 5) {
        $('table tbody tr:nth-last-child(-n+4) td:first-child div').removeClass('dropdown');
        $('table tbody tr:nth-last-child(-n+4) td:first-child div').addClass('dropup');
    } else if ($('table tbody tr').length >= 10) {
        $('table tbody tr:nth-last-child(-n+4) td:first-child div').removeClass('dropdown');
        $('table tbody tr:nth-last-child(-n+4) td:first-child div').addClass('dropup');
    } else if ($('table tbody tr').length >= 5) {
        $('table tbody tr:nth-last-child(-n+2) td:first-child div').removeClass('dropdown');
        $('table tbody tr:nth-last-child(-n+2) td:first-child div').addClass('dropup');
    }

    dropdown = event.currentTarget.closest('.dropdown') || event.currentTarget.closest('.dropup');

    const table = event.currentTarget.closest('table');

    if (dropdown && !dropdown.classList.contains('open')) {
        dropdown.style.position = 'static';
        const btnDropDown = dropdown.querySelector('.dropdown-toggle');
        const listHolder = dropdown.querySelector('.dropdown-menu');
        const tableRect = table.getBoundingClientRect();
        if (dropdown.classList.contains('dropup')) {
            listHolder.style.bottom = tableRect.bottom - btnDropDown.getBoundingClientRect().bottom + btnDropDown.offsetHeight + 20 + 'px';
        } else {
            listHolder.style.top = btnDropDown.getBoundingClientRect().top + btnDropDown.offsetHeight - tableRect.top + 'px';
        }
        listHolder.style.left = btnDropDown.offsetLeft + 'px';
        listHolder.dataset.open = true;
    }
};

watch(
    departmentAndAssignedSelectRef,
    (newVal) => {
        if (isDepartmentEnabled.value) {
            departmentAndAssignedSelectRef.value?.updateActiveDepartment({ id: 'allStaffs', name: 'All Staffs' });
        }
    },
    { immediate: true }
);

onMounted(() => {
    init();
    getDepartment();
    component_done_loading.value = true;

    document.querySelector('.table-responsive')?.addEventListener('scroll', (e) => {
        document.querySelectorAll('.dropdown-menu[data-open="true"]').forEach((menu) => {
            const button = menu.parentElement.querySelector('.dropdown-toggle');
            if (button) menu.style.left = button.offsetLeft - e.target.scrollLeft + 'px';
        });
    });
});
</script>
